import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const PORTAL_HOME = { contractor: "/contractor", vendor: "/vendor", staff: "/staff" };

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) return setError("Passwords do not match");
    if (password.length < 8) return setError("Password must be at least 8 characters");
    setLoading(true);
    try {
      const user = await resetPassword(token, password);
      navigate(PORTAL_HOME[user.portal] || "/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-2 text-slate-900 font-bold text-[16px] mb-10">
          <Building2 size={20} className="text-sky-500" />
          ScapeSource
        </div>

        {!token ? (
          <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="missing-token-error">
            This reset link is missing its token. Request a new one from the sign-in page.
          </div>
        ) : (
          <>
            <h1 className="text-[26px] font-extrabold tracking-tight text-slate-900">Choose a new password</h1>
            <p className="text-slate-500 text-sm mt-1.5 mb-7">Set a new password for your ScapeSource account.</p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-[13px] font-medium text-slate-700">New password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="reset-password-input"
                  className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                />
              </div>
              <div>
                <label className="text-[13px] font-medium text-slate-700">Confirm new password</label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="reset-confirm-password-input"
                  className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                />
              </div>

              {error && (
                <div className="text-[12.5px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="reset-password-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-password-submit-button"
                className="w-full h-11 rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold text-[14px] transition-colors disabled:opacity-60"
              >
                {loading ? "Saving…" : "Reset password"}
              </button>
            </form>
          </>
        )}

        <Link to="/login" className="block text-[13px] text-sky-600 font-medium mt-6">
          ← Back to sign in
        </Link>
      </div>
    </div>
  );
}
